import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../ui/Modal";
import { Clock } from "../icons";
import { formatTime } from "../../../utils/countryLocaleMap";
import { useWidgetLocale } from "../hooks/useWidgetLocale";
import { isBookingWithin24Hours } from "../../utils/bookingRules";
import type { Availability, AlternativeVenueTime } from "../../../common/jsonRpcTypes";
import type { CoreBookingDetails } from "../hooks/useBookingCreate";
import { CTA_FULL_WIDTH } from "../buttons/ctaClassNames";
import "../../styles/cross-sell-modal.scss";

interface AvailabilityFeedbackProps {
  readonly availability: Availability;
  readonly updateBooking: (details: Partial<CoreBookingDetails>) => Promise<void>;
  readonly onAccept: () => void;
  readonly onEnquiry: () => void;
  readonly onChangeDetails: () => void;
  readonly countryCode: string;
  readonly date: string;
  readonly time: string;
}

interface FeedbackModalProps {
  readonly show: boolean;
  readonly title: string;
  readonly testId: string;
  readonly continueLabel: string;
  readonly changeDetailsLabel: string;
  readonly onContinue: () => void;
  readonly onChangeDetails: () => void;
  readonly children: React.ReactNode;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  show,
  title,
  testId,
  continueLabel,
  changeDetailsLabel,
  onContinue,
  onChangeDetails,
  children,
}) => (
  <Modal show={show} onHide={onChangeDetails} centered dialogClassName="cross-sell-modal" ariaLabel={title}>
    <div data-testid={testId}>
      <Modal.Header>
        <Modal.Title className="gcw:text-xl gcw:font-bold gcw:text-text-primary">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="gcw:flex gcw:flex-col gcw:gap-bs-3 gcw:py-bs-3 gcw:text-body gcw:text-text-primary">
        {children}
      </Modal.Body>
      <Modal.Footer className="gcw:flex gcw:flex-col gcw:gap-4 gcw:pt-bs-3">
        <button
          type="button"
          className={`gcw:inline-flex gcw:items-center gcw:justify-center gcw:border gcw:border-primary gcw:bg-primary gcw:px-bs-3 gcw:font-medium gcw:text-white gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 ${CTA_FULL_WIDTH}`}
          onClick={onContinue}
        >
          {continueLabel}
        </button>
        <button
          type="button"
          className={`gcw:border gcw:border-primary gcw:bg-white gcw:px-bs-3 gcw:font-medium gcw:text-primary gcw:transition-colors gcw:hover:bg-primary gcw:hover:text-white gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 ${CTA_FULL_WIDTH}`}
          onClick={onChangeDetails}
        >
          {changeDetailsLabel}
        </button>
      </Modal.Footer>
    </div>
  </Modal>
);

/**
 * AvailabilityFeedback — displays the availability status for the selected
 * booking slot and shows alternative time/venue suggestions when the
 * requested slot is unavailable.
 *
 * Handles all five availability actions:
 * - `accept`        — booking can be auto-confirmed
 * - `reject`        — not available, offer enquiry or change details
 * - `suggest_times` — alternative time slots offered
 * - `may_enquire`   — venue appears full but enquiry is possible
 * - `enquire`       — booking will be submitted as an enquiry
 */
const AvailabilityFeedback: React.FC<AvailabilityFeedbackProps> = ({
  availability,
  updateBooking,
  onAccept,
  onEnquiry,
  onChangeDetails,
  countryCode,
  date,
  time,
}) => {
  const { t } = useTranslation();
  const timeLocale = useWidgetLocale(countryCode);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showModal, setShowModal] = useState(true);

  const { action, data } = availability;
  const alternativeTimes = data?.alternative_times ?? [];
  const alternativeVenueTimes = data?.alternative_venue_times ?? [];
  const noAvailabilityAction = data?.no_availability_action;

  const handleChangeDetails = () => {
    setShowModal(false);
    onChangeDetails();
  };

  const handleContinue = () => {
    setShowModal(false);
    if (action === "accept") {
      onAccept();
      return;
    }
    onEnquiry();
  };

  /**
   * Extract HH:mm from a time string for API submission.
   * Handles both ISO date strings and plain HH:mm values.
   */
  const extractTimeForApi = (timeStr: string): string => {
    if (timeStr.includes("T")) {
      const d = new Date(timeStr);
      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    }
    return timeStr;
  };

  /**
   * Handle selecting an alternative time slot.
   * Calls updateBooking with the new time (HH:mm format) and shows
   * a loading state while the API re-checks availability.
   */
  const handleSelectAlternativeTime = async (timeStart: string) => {
    setIsUpdating(true);
    try {
      await updateBooking({ time: extractTimeForApi(timeStart) });
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * Handle selecting an alternative venue/time slot.
   * Also propagates the booking type from the cross-sell response so the
   * final submission uses the correct type for the selected venue.
   */
  const handleSelectAlternativeVenueTime = async (
    slotStart: string,
    venueId: string,
    type?: string | { id: string; name: string },
  ) => {
    setIsUpdating(true);
    try {
      const typeId = typeof type === "object" ? type.id : type;
      await updateBooking({
        time: extractTimeForApi(slotStart),
        venue_id: venueId,
        ...(typeId ? { type: typeId } : {}),
      });
    } finally {
      setIsUpdating(false);
    }
  };

  if (isUpdating) {
    return (
      <div className="gcw:flex gcw:justify-center gcw:py-bs-3" aria-live="polite" data-testid="availability-loading">
        <div className="loading-spinner" />
        <span className="gcw:sr-only">{t("loading")}</span>
      </div>
    );
  }

  if (action === "accept") {
    return (
      <FeedbackModal
        show={showModal}
        title={t("booking_availability_accepted_title")}
        testId="availability-accepted"
        continueLabel={t("booking_next")}
        changeDetailsLabel={t("booking_change_details")}
        onContinue={handleContinue}
        onChangeDetails={handleChangeDetails}
      >
        <p>{t("booking_availability_accepted")}</p>
      </FeedbackModal>
    );
  }

  if (action === "enquire") {
    const showTimeWarning = isBookingWithin24Hours(date, time);
    return (
      <FeedbackModal
        show={showModal}
        title={t("booking_availability_enquiry_title")}
        testId="availability-enquire"
        continueLabel={t("booking_continue_as_enquiry")}
        changeDetailsLabel={t("booking_change_details")}
        onContinue={handleContinue}
        onChangeDetails={handleChangeDetails}
      >
        {showTimeWarning && (
          <div
            className="gcw:flex gcw:items-start gcw:gap-2 gcw:rounded-md gcw:border gcw:border-warning gcw:bg-amber-50 gcw:p-bs-3 gcw:text-amber-800 widget-warning-panel"
            data-testid="availability-time-warning"
          >
            <Clock size={16} className="gcw:mt-0.5 gcw:shrink-0 widget-panel-icon" aria-hidden="true" />
            <span>{t("booking_time_warning")}</span>
          </div>
        )}
        <p>{t("booking_availability_enquiry_message")}</p>
      </FeedbackModal>
    );
  }

  if (action === "reject") {
    return (
      <FeedbackModal
        show={showModal}
        title={t("booking_availability_rejected_title")}
        testId="availability-rejected"
        continueLabel={t("booking_continue_as_enquiry")}
        changeDetailsLabel={t("booking_change_details")}
        onContinue={handleContinue}
        onChangeDetails={handleChangeDetails}
      >
        <p>{t("booking_availability_rejected")}</p>
      </FeedbackModal>
    );
  }

  if (action === "may_enquire") {
    return (
      <FeedbackModal
        show={showModal}
        title={t("booking_availability_may_enquire_title")}
        testId="availability-may-enquire"
        continueLabel={t("booking_continue_as_enquiry")}
        changeDetailsLabel={t("booking_change_details")}
        onContinue={handleContinue}
        onChangeDetails={handleChangeDetails}
      >
        <p>{t("booking_availability_may_enquire")}</p>
      </FeedbackModal>
    );
  }

  return (
    <Modal
      show={showModal}
      onHide={handleChangeDetails}
      centered
      dialogClassName="cross-sell-modal"
      ariaLabel={t("booking_availability_unavailable_title")}
    >
      <div data-testid="availability-suggest-times">
        <Modal.Header>
          <Modal.Title className="gcw:text-xl gcw:font-bold gcw:text-text-primary">
            {t("booking_availability_unavailable_title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="gcw:flex gcw:max-h-[60vh] gcw:flex-col gcw:gap-bs-3 gcw:overflow-y-auto gcw:py-bs-3">
          <p>{t("booking_availability_unavailable")}</p>

          {noAvailabilityAction === "may_enquire" && (
            <button
              type="button"
              className={`gcw:inline-flex gcw:items-center gcw:justify-center gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 ${CTA_FULL_WIDTH}`}
              onClick={onEnquiry}
            >
              {t("booking_enquiry_for_time")}
            </button>
          )}

          {alternativeTimes.length > 0 && (
            <div>
              <h3 className="gcw:font-bold gcw:text-text-primary gcw:mb-2">{t("booking_alternative_times")}</h3>
              <ul className="gcw:flex gcw:flex-wrap gcw:gap-2" aria-label={t("booking_alternative_times")}>
                {alternativeTimes.map((alt) => {
                  const startLabel = formatTime(extractTimeForApi(alt.start), date, countryCode, timeLocale);
                  const endLabel = formatTime(extractTimeForApi(alt.end), date, countryCode, timeLocale);
                  const timeLabel = `${startLabel} - ${endLabel}`;

                  return (
                    <li key={`${alt.start}-${alt.end}`}>
                      <button
                        type="button"
                        className="gcw:rounded-md gcw:border gcw:border-primary gcw:bg-white gcw:px-bs-3 gcw:py-1 gcw:text-md gcw:text-primary gcw:transition-colors gcw:hover:bg-primary gcw:hover:text-white gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:disabled:opacity-50 gcw:disabled:cursor-not-allowed"
                        disabled={!alt.available}
                        onClick={() => handleSelectAlternativeTime(alt.start)}
                        aria-label={t("booking_select_time_slot", { time: timeLabel })}
                      >
                        {timeLabel}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {alternativeVenueTimes.length > 0 && (
            <div>
              <h3 className="gcw:font-medium gcw:text-text-primary gcw:mb-2">{t("booking_alternative_venues")}</h3>
              <ul className="gcw:flex gcw:flex-col gcw:gap-2" aria-label={t("booking_alternative_venues")}>
                {alternativeVenueTimes.map((alt: AlternativeVenueTime, idx: number) => {
                  const venueName = alt.venue?.title ?? "";
                  const venueStreet = alt.venue?.street ?? "";
                  const venuePostcode = alt.venue?.postcode ?? "";
                  const altVenueId = alt.venue?._id ?? "";
                  const slots = alt.slots ?? [];

                  return (
                    <li
                      key={`venue-${altVenueId || idx}`}
                      className="gcw:border gcw:border-border gcw:rounded-md gcw:p-bs-3"
                    >
                      <div className="gcw:mb-2">
                        <span className="gcw:font-bold gcw:text-text-primary">{venueName}</span>
                        {venueStreet && (
                          <span className="gcw:ml-2 gcw:text-text-muted">
                            {venueStreet}
                            {venuePostcode ? `, ${venuePostcode}` : ""}
                            {alt.distance != null ? ` (${alt.distance.toFixed(1)} miles)` : ""}
                          </span>
                        )}
                      </div>
                      {slots.length > 0 && (
                        <div className="gcw:flex gcw:flex-wrap gcw:gap-2">
                          {slots.map((slot) => {
                            const slotLabel = formatTime(extractTimeForApi(slot.start), date, countryCode, timeLocale);

                            return (
                              <button
                                key={slot.start}
                                type="button"
                                className="gcw:rounded-md gcw:border gcw:border-primary gcw:bg-white gcw:px-bs-3 gcw:py-1 gcw:text-md gcw:text-primary gcw:transition-colors gcw:hover:bg-primary gcw:hover:text-white gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:disabled:opacity-50 gcw:disabled:cursor-not-allowed"
                                disabled={!slot.available}
                                onClick={() =>
                                  handleSelectAlternativeVenueTime(slot.start, altVenueId, alt.booking?.type)
                                }
                                aria-label={t("booking_select_venue_time_slot", {
                                  venue: venueName,
                                  time: slotLabel,
                                })}
                              >
                                {slotLabel}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="gcw:flex gcw:flex-col gcw:gap-4 gcw:pt-bs-3">
          <button
            type="button"
            className={`gcw:border gcw:border-primary gcw:bg-white gcw:px-bs-3 gcw:font-medium gcw:text-primary gcw:transition-colors gcw:hover:bg-primary gcw:hover:text-white gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 ${CTA_FULL_WIDTH}`}
            onClick={onEnquiry}
          >
            {t("booking_enquiry_instead")}
          </button>
          <button
            type="button"
            className={`gcw:border gcw:border-primary gcw:bg-white gcw:px-bs-3 gcw:font-medium gcw:text-primary gcw:transition-colors gcw:hover:bg-primary gcw:hover:text-white gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 ${CTA_FULL_WIDTH}`}
            onClick={handleChangeDetails}
          >
            {t("booking_change_details")}
          </button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default AvailabilityFeedback;
